// A panel: command line with autocomplete, title bar with a live quote for
// the loaded security, the function's body, and a status line.

import { quotes, search, type Quote } from "./api";
import { clear, errorBox, h } from "./dom";
import * as f from "./fmt";
import { allFns, lookup, parse, type Ctx, type FnDef, type PanelApi } from "./registry";
import { display, localMatches } from "./securities";

export interface PanelHost {
  focus(p: Panel): void;
  next(p: Panel): Panel;
  save(): void;
}

interface Suggestion {
  key: string;
  label: string;
  tag: string;
  /// Replacement for the whole command line.
  apply: string;
  /// Run immediately on pick instead of just filling the line.
  run?: boolean;
}

const QUOTE_MS = 15_000;

export class Panel {
  readonly el: HTMLElement;
  readonly input: HTMLInputElement;
  private suggestBox: HTMLElement;
  private titleEl: HTMLElement;
  private quoteEl: HTMLElement;
  private bodyEl: HTMLElement;
  private statusEl: HTMLElement;

  sec: string | null = null;
  cmd = "";
  private history: string[] = [];
  private mount: AbortController | null = null;
  private actions: (string | (() => void) | null)[] = [];
  private suggestions: Suggestion[] = [];
  private sel = -1;
  private searchTimer = 0;
  private searchAbort: AbortController | null = null;

  constructor(
    readonly index: number,
    private host: PanelHost,
  ) {
    this.input = h("input", {
      spellcheck: "false",
      autocomplete: "off",
      placeholder: "Type a ticker or function, then Enter.  HELP for the list",
    });
    this.suggestBox = h("div", { class: "suggest", style: { display: "none" } });
    const go = h("button", { class: "go", onclick: () => this.submit() }, "GO");
    this.titleEl = h("div", { class: "tl" });
    this.quoteEl = h("div", { class: "q" });
    this.bodyEl = h("div", { class: "body" });
    this.statusEl = h("div", { class: "status" });
    this.el = h(
      "div",
      { class: "panel" },
      h("div", { class: "cmdbar" }, h("span", { class: "pn" }, String(index + 1)), this.input, go, this.suggestBox),
      h("div", { class: "titlebar" }, this.titleEl, this.quoteEl),
      this.bodyEl,
      this.statusEl,
    );
    this.el.addEventListener("mousedown", () => this.host.focus(this));
    this.input.addEventListener("keydown", (e) => this.onKey(e));
    this.input.addEventListener("input", () => this.onInput());
    this.input.addEventListener("blur", () => setTimeout(() => this.hideSuggest(), 150));
  }

  // --- commands -----------------------------------------------------------

  private submit(): void {
    const text = this.input.value.trim();
    if (this.sel >= 0 && this.suggestions[this.sel]) {
      this.pick(this.suggestions[this.sel]);
      return;
    }
    if (!text) return;
    this.input.value = "";
    this.hideSuggest();
    this.run(text);
  }

  /// Run a command. Bad input shows an error without losing the current view.
  run(text: string, record = true): void {
    const p = parse(text, this.sec);
    if (p.kind === "error") {
      if (p.msg) this.flashError(p.msg);
      return;
    }
    if (p.kind === "action") {
      const a = this.actions[p.n - 1];
      if (!a) this.flashError(`No item ${p.n}`);
      else if (typeof a === "string") this.run(a);
      else a();
      return;
    }
    const canonical = [p.sec && p.fn.needsSec ? p.sec : null, p.fn.code, ...p.args].filter(Boolean).join(" ");
    if (record && this.cmd && this.cmd !== canonical) this.history.push(this.cmd);
    if (this.history.length > 50) this.history.shift();
    this.cmd = canonical;
    this.start(p.fn, p.sec, p.args);
    this.host.save();
  }

  back(): void {
    const prev = this.history.pop();
    if (prev) this.run(prev, false);
  }

  private flashError(msg: string): void {
    this.status(msg);
    this.statusEl.classList.add("down");
    setTimeout(() => this.statusEl.classList.remove("down"), 2500);
  }

  private start(fn: FnDef, sec: string | null, args: string[]): void {
    this.mount?.abort();
    const mount = new AbortController();
    this.mount = mount;
    this.actions = [];
    const secChanged = sec !== this.sec;
    this.sec = sec;
    clear(this.bodyEl);
    this.bodyEl.scrollTop = 0;
    this.status("");

    clear(
      this.titleEl,
      fn.needsSec && sec ? h("span", { class: "sec" }, display(sec)) : null,
      h("span", { class: "fn" }, fn.code),
      " ",
      h("span", { class: "fname" }, fn.name),
    );
    if (!fn.needsSec) clear(this.quoteEl);
    else if (secChanged || !this.quoteEl.firstChild) clear(this.quoteEl);

    const api: PanelApi = {
      go: (cmd) => this.run(cmd),
      goOther: (cmd) => this.host.next(this).run(cmd),
      action: (cmd, at) => {
        if (mount.signal.aborted) return 0;
        if (at === undefined) return this.actions.push(cmd);
        this.actions[at - 1] = cmd;
        return at;
      },
      status: (text, meta) => {
        if (!mount.signal.aborted) this.status(text, meta);
      },
      every: (ms, fn) => every(ms, fn, mount.signal),
    };
    const ctx: Ctx = { sec, args, body: this.bodyEl, signal: mount.signal, panel: api };
    if (fn.needsSec && sec) this.titleQuote(sec, fn, mount.signal);
    // Deferred so a run replaced in the same tick (startup, deep links)
    // never starts.
    Promise.resolve()
      .then(() => (mount.signal.aborted ? undefined : fn.run(ctx)))
      .catch((e) => {
        if (mount.signal.aborted) return;
        clear(this.bodyEl, errorBox(e));
      });
  }

  /// Keep the title bar's security name and last price current.
  private titleQuote(sec: string, fn: FnDef, signal: AbortSignal): void {
    const paint = (q: Quote) => {
      clear(
        this.titleEl,
        h("span", { class: "sec" }, display(sec, q.quoteType)),
        h("span", { class: "dim" }, q.shortName ?? q.longName ?? ""),
        h("span", { class: "fn" }, fn.code),
        h("span", { class: "fname" }, fn.name),
      );
      const ch = q.regularMarketChange;
      clear(
        this.quoteEl,
        h("span", { class: "dim" }, q.marketState === "REGULAR" ? "" : (q.marketState ?? "").replace("PREPRE", "PRE")),
        h("span", { style: { fontWeight: "bold" } }, f.price(q.regularMarketPrice, q.priceHint)),
        h("span", { class: f.cls(ch) }, f.chg(ch, q.priceHint)),
        h("span", { class: f.cls(ch) }, f.pct(q.regularMarketChangePercent)),
        h("span", { class: "dim" }, q.currency ?? ""),
      );
    };
    const load = async () => {
      try {
        const r = await quotes([sec], signal);
        if (!signal.aborted && r.data[0]) paint(r.data[0]);
      } catch {
        // the body reports errors; the title just keeps its last value
      }
    };
    load();
    every(QUOTE_MS, load, signal);
  }

  status(text: string, meta?: { asof?: number; stale?: boolean }): void {
    clear(
      this.statusEl,
      meta?.stale ? h("span", { class: "stale" }, "STALE") : null,
      h("span", null, text),
      meta?.asof ? h("span", null, `as of ${f.time(meta.asof, true)}`) : null,
    );
  }

  // --- autocomplete -------------------------------------------------------

  private onInput(): void {
    const text = this.input.value.toUpperCase();
    this.sel = -1;
    if (!text.trim()) return this.hideSuggest();
    const toks = text.trim().split(/\s+/);
    const last = toks[toks.length - 1];
    const prefix = toks.slice(0, -1).join(" ");
    const withPrefix = (s: string) => (prefix ? `${prefix} ${s}` : s);

    const fns: Suggestion[] = allFns()
      .filter((d) => d.code.startsWith(last) || (last.length >= 3 && d.name.toUpperCase().includes(last)))
      .slice(0, 6)
      .map((d) => ({ key: d.code, label: d.name, tag: "function", apply: withPrefix(d.code), run: true }));
    const secs: Suggestion[] = localMatches(text.trim()).map((a) => ({
      key: a.bbg.replace(/ (Index|Comdty|Curncy)$/, ""),
      label: a.name,
      tag: a.bbg.split(" ").pop()!,
      apply: a.yahoo,
    }));
    this.show([...fns, ...secs]);

    clearTimeout(this.searchTimer);
    this.searchAbort?.abort();
    if (toks.length > 2 || text.trim().length < 2 || lookup(last)) return;
    this.searchTimer = window.setTimeout(async () => {
      const ac = new AbortController();
      this.searchAbort = ac;
      try {
        const r = await search(text.trim(), ac.signal);
        if (ac.signal.aborted || this.input.value.toUpperCase() !== text) return;
        const remote: Suggestion[] = r.data.slice(0, 10).map((s) => ({
          key: s.symbol,
          label: s.name ?? "",
          tag: [s.type, s.exchange].filter(Boolean).join(" · "),
          apply: s.symbol,
        }));
        const seen = new Set([...fns, ...secs].map((s) => s.apply));
        this.show([...fns, ...secs, ...remote.filter((s) => !seen.has(s.apply))]);
      } catch {
        // autocomplete is best effort
      }
    }, 220);
  }

  private show(list: Suggestion[]): void {
    this.suggestions = list;
    if (this.sel >= list.length) this.sel = -1;
    clear(
      this.suggestBox,
      ...list.map((s, i) =>
        h(
          "div",
          { class: i === this.sel ? "sel" : undefined, onmousedown: (e: MouseEvent) => (e.preventDefault(), this.pick(s)) },
          h("span", { class: "k" }, s.key),
          h("span", null, s.label),
          h("span", { class: "t" }, s.tag),
        ),
      ),
    );
    this.suggestBox.style.display = list.length ? "" : "none";
  }

  private hideSuggest(): void {
    this.suggestions = [];
    this.sel = -1;
    this.suggestBox.style.display = "none";
    clearTimeout(this.searchTimer);
  }

  private pick(s: Suggestion): void {
    this.hideSuggest();
    if (s.run) {
      this.input.value = "";
      this.run(s.apply);
    } else {
      this.input.value = s.apply + " ";
      this.input.focus();
    }
  }

  private onKey(e: KeyboardEvent): void {
    const open = this.suggestions.length > 0;
    switch (e.key) {
      case "Enter":
        e.preventDefault();
        this.submit();
        break;
      case "ArrowDown":
      case "ArrowUp":
        if (!open) break;
        e.preventDefault();
        this.sel = (this.sel + (e.key === "ArrowDown" ? 1 : -1) + this.suggestions.length + 1) % (this.suggestions.length + 1);
        if (this.sel === this.suggestions.length) this.sel = -1;
        this.show(this.suggestions);
        break;
      case "Tab":
        if (!open) break;
        e.preventDefault();
        this.pick(this.suggestions[Math.max(0, this.sel)]);
        break;
      case "Escape":
        e.preventDefault();
        if (open) this.hideSuggest();
        else this.input.value = "";
        break;
      case "Backspace":
        if (!this.input.value) {
          e.preventDefault();
          this.back();
        }
        break;
      case "PageDown":
      case "PageUp":
        e.preventDefault();
        this.bodyEl.scrollBy({ top: (e.key === "PageDown" ? 1 : -1) * this.bodyEl.clientHeight * 0.9 });
        break;
    }
  }
}

/// Repeat `fn` every `ms` until `signal` aborts, skipping while the tab is
/// hidden and catching up as soon as it is visible again.
export function every(ms: number, fn: () => unknown, signal: AbortSignal): void {
  if (signal.aborted) return;
  let last = Date.now();
  const tick = () => {
    if (signal.aborted || document.visibilityState !== "visible") return;
    if (Date.now() - last < ms - 50) return;
    last = Date.now();
    Promise.resolve().then(fn).catch(() => {});
  };
  const id = setInterval(tick, Math.min(ms, 5000));
  const onVis = () => tick();
  document.addEventListener("visibilitychange", onVis);
  signal.addEventListener("abort", () => {
    clearInterval(id);
    document.removeEventListener("visibilitychange", onVis);
  });
}
